import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DAMAGE_TYPES = [
  { id: "water", label: "Water damage" },
  { id: "fire", label: "Fire / smoke" },
  { id: "wind", label: "Wind / storm" },
  { id: "hail", label: "Hail" },
  { id: "mold", label: "Mold" },
  { id: "vandalism", label: "Theft / vandalism" },
  { id: "other", label: "Other" },
];

export default function OnboardingWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [carriers, setCarriers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    zip_code: "",
    carrier: "",
    policy_number: "",
    date_of_loss: "",
    damage_type: "",
    description: "",
  });

  useEffect(() => {
    api.get("/carriers").then((r) => setCarriers(r.data));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const stepValid = () => {
    if (step === 0)
      return data.full_name && data.phone && data.email && data.address && data.zip_code;
    if (step === 1) return data.carrier && data.policy_number;
    if (step === 2)
      return data.date_of_loss && data.damage_type && data.description.length > 10;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/claims", data);
      toast.success("Claim filed — let's add evidence");
      navigate(`/claim/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitles = [
    { tag: "01 Identity", title: "Tell us who you are" },
    { tag: "02 Policy", title: "Your insurance policy" },
    { tag: "03 The Event", title: "Describe the loss" },
  ];

  return (
    <div
      className="max-w-3xl mx-auto px-6 py-12"
      data-testid="onboarding-wizard"
    >
      <div className="mb-10">
        <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
          {stepTitles[step].tag}
        </p>
        <h1 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight">
          {stepTitles[step].title}
        </h1>
        <Progress
          value={((step + 1) / 3) * 100}
          className="mt-6 h-1.5"
          data-testid="onboarding-progress"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8">
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                value={data.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                className="mt-1"
                data-testid="wizard-fullname-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={data.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-1"
                data-testid="wizard-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1"
                data-testid="wizard-email-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address">Property address</Label>
              <Input
                id="address"
                value={data.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="123 Main St, Miami, FL"
                className="mt-1"
                data-testid="wizard-address-input"
              />
            </div>
            <div>
              <Label htmlFor="zip">Zip code</Label>
              <Input
                id="zip"
                value={data.zip_code}
                onChange={(e) => set("zip_code", e.target.value)}
                placeholder="33101"
                className="mt-1"
                data-testid="wizard-zip-input"
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label>Insurance carrier</Label>
              <Select
                value={data.carrier}
                onValueChange={(v) => set("carrier", v)}
              >
                <SelectTrigger
                  className="mt-1"
                  data-testid="wizard-carrier-select"
                >
                  <SelectValue placeholder="Choose your carrier" />
                </SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="policy_number">Policy number</Label>
              <Input
                id="policy_number"
                value={data.policy_number}
                onChange={(e) => set("policy_number", e.target.value)}
                placeholder="e.g. SF-44218801"
                className="mt-1"
                data-testid="wizard-policy-input"
              />
            </div>
            {data.carrier && (
              <div className="rounded-lg bg-[#F9F8F6] border border-[#E5E7EB] p-4 text-sm text-gray-700">
                <CheckCircle2 className="inline w-4 h-4 mr-2 text-[#1E3F33]" />
                We'll surface a carrier-specific guide on the next screen with
                exact Proof of Loss timing.
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <Label htmlFor="dol">Date of loss</Label>
              <Input
                id="dol"
                type="date"
                value={data.date_of_loss}
                onChange={(e) => set("date_of_loss", e.target.value)}
                className="mt-1"
                data-testid="wizard-dateofloss-input"
              />
            </div>
            <div>
              <Label>Type of damage</Label>
              <Select
                value={data.damage_type}
                onValueChange={(v) => set("damage_type", v)}
              >
                <SelectTrigger
                  className="mt-1"
                  data-testid="wizard-damage-select"
                >
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent>
                  {DAMAGE_TYPES.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="desc">Briefly describe what happened</Label>
              <Textarea
                id="desc"
                value={data.description}
                onChange={(e) => set("description", e.target.value)}
                rows={5}
                className="mt-1"
                placeholder="On Saturday night a pipe burst above the kitchen ceiling. Drywall is sagging and the floor is wet…"
                data-testid="wizard-description-input"
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="wizard-back-button"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {step < 2 ? (
          <Button
            disabled={!stepValid()}
            onClick={() => setStep((s) => s + 1)}
            className="btn-primary rounded-full px-7"
            data-testid="wizard-next-button"
          >
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            disabled={!stepValid() || submitting}
            onClick={submit}
            className="btn-primary rounded-full px-7"
            data-testid="wizard-submit-button"
          >
            {submitting ? "Filing claim…" : "File claim"}
          </Button>
        )}
      </div>
    </div>
  );
}
